import { AvatarImage } from "@/components/primitives/avatar";
import { processAvatarBucketUrl } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

type CachedAvatarImageProps = {
  attachmentId?: string;
};

export default function CachedAvatarImage({
  attachmentId,
}: CachedAvatarImageProps) {
  const [retryCount, setRetryCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setRetryCount(0);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [attachmentId]);

  const attachmentUrl = attachmentId
    ? processAvatarBucketUrl(attachmentId)
    : "";

  if (!attachmentUrl) {
    return null;
  }

  const separator = attachmentUrl.includes("?") ? "&" : "?";
  const src =
    retryCount > 0
      ? `${attachmentUrl}${separator}r=${retryCount}`
      : attachmentUrl;

  return (
    <AvatarImage
      key={src}
      src={src}
      onLoadingStatusChange={(status) => {
        if (
          status === "error" &&
          retryCount < MAX_RETRIES &&
          !timerRef.current
        ) {
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setRetryCount((c) => c + 1);
          }, RETRY_DELAY_MS);
        }
      }}
    />
  );
}
